import { ArrowRightIcon, ClipboardListIcon } from 'lucide-react';
import { useId } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { useLead } from '@/features/leads/api/get-lead/get-lead.hook';
import {
  QuestionLabel,
  answeredValues,
  formatSubmittedAt,
} from './lead-form-parts';
import { type FormResponse, getLeadFormResponse } from './lead-form-response';

/** At a glance is a preview: the rest of the form is one click away. */
export const PREVIEW_QUESTIONS = 3;

/**
 * The lead's Meta form in At a glance: which form, when, and the first few
 * questions they answered, each clamped to two lines. "Open full form" opens
 * the whole saved response in a dialog without leaving the customer. Name, email and phone are skipped here
 * because the contact facts above already show them.
 */
export function LeadFormGlance({
  leadId,
  onOpen,
}: { leadId: string; onOpen: () => void }) {
  const headingId = useId();
  const { lead, isLoading, isError, refetch } = useLead({ leadId });
  const response = lead ? getLeadFormResponse(lead) : null;
  const answerCount =
    response?.answers.filter((a) => a.status === 'answered').length ?? 0;

  return (
    <section aria-labelledby={headingId} className="space-y-3 border-t pt-4">
      <div className="flex items-center gap-2.5">
        <ClipboardListIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <h3 id={headingId} className="font-medium text-sm">
          Lead form
        </h3>
      </div>

      {/* pl-6 = icon + gap: text lines up with the contact facts above. */}
      <div className="space-y-3 pl-6">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : isError ? (
          <div className="space-y-1">
            <p className="text-sm">Couldn't load the form answers.</p>
            <Button
              size="sm"
              variant="link"
              className="h-auto p-0"
              onClick={() => refetch()}
            >
              Try again
            </Button>
          </div>
        ) : !response ? (
          <p className="text-muted-foreground text-sm">
            No lead form submitted.
          </p>
        ) : (
          <>
            <Preview response={response} />
            <div className="space-y-1.5">
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-between border-primary/15 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary"
                data-lead-form-trigger=""
                aria-haspopup="dialog"
                onClick={onOpen}
              >
                Open full form
                <ArrowRightIcon aria-hidden className="size-3.5" />
              </Button>
              <p className="text-center text-muted-foreground text-xs">
                {answerCount} {answerCount === 1 ? 'answer' : 'answers'} on
                record
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Preview({ response }: { response: FormResponse }) {
  const submittedAt = formatSubmittedAt(
    response.submittedAt,
    'd MMM yyyy, HH:mm'
  );
  const preview = response.answers
    .filter((a) => !a.isContact && answeredValues(a).length > 0)
    .slice(0, PREVIEW_QUESTIONS);

  return (
    <>
      <div>
        <p className="break-words font-medium text-sm leading-snug">
          {response.formName ?? 'Form name not available'}
        </p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          {submittedAt
            ? `Submitted ${submittedAt}`
            : 'Submission time not available'}
        </p>
      </div>

      {preview.length > 0 ? (
        <dl className="space-y-2.5">
          {preview.map((answer, index) => (
            <div key={`${index}-${answer.key}`}>
              <dt className="line-clamp-2 break-words text-muted-foreground text-xs leading-snug">
                <QuestionLabel answer={answer} />
              </dt>
              <dd className="mt-0.5 line-clamp-2 whitespace-pre-line break-words text-sm leading-snug">
                {answeredValues(answer).join(', ')}
              </dd>
            </div>
          ))}
        </dl>
      ) : response.answers.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No form answers on record.
        </p>
      ) : null}
    </>
  );
}
